import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSportsbook } from "../../../providers/SportsbookContext";
import { AppLoader } from "../../../components/ui/loaders/AppLoader";
import { IoReload } from "react-icons/io5";

const Sportsbook = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [headerHeight, setHeaderHeight] = useState(0);
  const [bottomBarHeight, setBottomBarHeight] = useState(0);
  const { sendMessageToSportsbook, sportsbookRef, sportsbookAction, setSportsbookAction, sportsbookUrl, loading, error } = useSportsbook();

  useEffect(() => {
    const updateHeights = () => {
      const header = document.getElementById("appHeader");
      const bottomBar = document.getElementById("appBar");

      if (header) setHeaderHeight(header.offsetHeight);
      if (bottomBar) setBottomBarHeight(bottomBar.offsetHeight);
    };

    updateHeights();
    window.addEventListener("resize", updateHeights);
    return () => window.removeEventListener("resize", updateHeights);
  }, []);

  useEffect(() => {
    const footer = document.getElementById("appFooter");
    const hideFooterRoutes = "/sportsbook";
    if (footer) {
      if (location.pathname.startsWith(hideFooterRoutes)) {
        footer.classList.add("hidden");
      } else {
        footer.classList.remove("hidden");
      }
    }

    return () => {
      if (footer) {
        footer.classList.remove("hidden");
      }
    };
  }, [location.pathname]);

  const handleIframeLoad = () => {
    if (!sportsbookRef.current || !sportsbookRef.current.contentWindow) {
      console.warn("⚠️ Iframe not ready yet. Skipping handleIframeLoad.");
      return;
    }

    let defaultPage;
    if (location.pathname === "/sportsbook/sports" || sportsbookAction.changePage === "sport") {
      defaultPage = "sport";
      navigate("/sportsbook/sports");
    } else if (location.pathname === "/sportsbook/live" || sportsbookAction.changePage === "live") {
      defaultPage = "live";
      navigate("/sportsbook/live");
    } else if (location.pathname.startsWith("/sportsbook/bet-history")) {
      defaultPage = "bet-history";
    }

    if (defaultPage) {
      console.log("📩 Sending changePage to Sportsbook:", defaultPage);
      setSportsbookAction({ changePage: defaultPage }); // Store action state
      setTimeout(() => sendMessageToSportsbook({ changePage: defaultPage }), 500);
    }
  };

  useEffect(() => {
    if (sportsbookRef.current && sportsbookRef.current.contentWindow) {
      console.log("🖼️ Iframe detected. Triggering handleIframeLoad.");
      setTimeout(handleIframeLoad, 500);
    } else {
      console.warn("⚠️ Iframe not detected yet. Retrying handleIframeLoad in 500ms.");
      setTimeout(handleIframeLoad, 500);
    }
  }, [location.pathname]);

  if (loading) return <AppLoader />;
  if (error)
    return (
      <div className="text-red-500 text-center mt-4 h-screen flex flex-col justify-center items-center">
        {error}{" "}
        <button className="flex items-center gap-1 text-white py-4" onClick={() => window.location.reload()}>
          <IoReload size={20} /> Reload
        </button>
      </div>
    );

  return (
    <div className="overflow-hidden fixed inset-0 flex flex-col">
      <div
        id="appcontent"
        className={`bg-body flex-1 w-full md:h-full  md:pb-2 pb-[${bottomBarHeight}px]`}
        style={{ paddingTop: `calc(${headerHeight}px + 10px`, paddingBottom: `calc(${bottomBarHeight}px` }}
      >
        {sportsbookUrl ? (
          <iframe
            title="Sportsbook"
            ref={sportsbookRef}
            src={sportsbookUrl}
            className={`w-full h-full border-none`}
            onError={(e) => console.error("Iframe failed to load:", e)}
            onLoad={() => {
              console.log("✅ Sportsbook Iframe Loaded!");
              setTimeout(() => {
                if (sportsbookAction) {
                  console.log("📩 Resending saved action:", sportsbookAction);
                  sendMessageToSportsbook(sportsbookAction);
                }
              }, 500);
            }}
          />
        ) : (
          <p className="text-center text-red-500">Failed to load sportsbook. Please try again.</p>
        )}
      </div>
    </div>
  );
};

export default Sportsbook;
